// ALTIORA Radar · Sprint 11 · PERSONNES canoniques
// Vue funnel : à découvrir → identifiées → coordonnées trouvées →
// vérifiées → autorisées → contactées → converties.
// Chaque carte affiche provenance, segment, canaux, Statut par canal.
import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Filter, Users, Phone, Mail, ShieldCheck, Layers,
          UserCheck, AlertTriangle, ArrowUpRight, Database, Zap,
          Fingerprint, ShieldOff, Award } from "lucide-react";
import { api } from "@/lib/api";
import { NAVY, GOLD, SLATE, CHAMPAGNE } from
  "@/components/altiora/PremiumUI";

const CAT_LABELS = {
  DECISION_MAKER: { bg: "#D8F5E5", color: "#0F5E39", label: "Décideur" },
  INDIVIDUAL_PROSPECT: { bg: "#E9F0FA", color: "#1E3A70", label: "Prospect ind." },
  PARTNER: { bg: "#FDECC8", color: "#8A5200", label: "Partenaire" },
  REFERRAL_SOURCE: { bg: "#F2E7FA", color: "#5B247A", label: "Référencement" },
  LOW_RELEVANCE: { bg: "#E5E7EB", color: "#64748B", label: "Faible" },
  OTHER: { bg: "#F5F5F5", color: "#8A8A8A", label: "Non classé" },
};

const CHANNEL_STATUS_COLOR = {
  UNKNOWN: "#9CA3AF", FOUND: "#F59E0B", VERIFIED: "#3B82F6",
  VERIFICATION_REQUIRED: "#F59E0B", AUTHORIZED: "#10B981",
  SUPPRESSED: "#EF4444", DO_NOT_CONTACT: "#EF4444",
  UNAVAILABLE: "#9CA3AF",
};

function ChannelPill({ channel, status }) {
  const c = CHANNEL_STATUS_COLOR[status] || "#9CA3AF";
  return (
    <span data-testid={`persons-channel-${channel}`}
          className="text-[10px] px-1.5 py-0.5 rounded font-mono uppercase"
          style={{ background: `${c}22`, color: c,
                     border: `1px solid ${c}66` }}>
      {channel} · {status.toLowerCase()}
    </span>
  );
}

export default function AltioraRadarPersons() {
  const [dash, setDash] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [running, setRunning] = useState(false);
  const [benchmark, setBenchmark] = useState(null);
  const [reservoir, setReservoir] = useState(null);
  const [factory, setFactory] = useState(null);
  const [proof, setProof] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [provenanceOpen, setProvenanceOpen] = useState(null);   // person_id
  const [provenanceData, setProvenanceData] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [d, list, res, fac, tab] = await Promise.all([
        api.get("/leads-engine/persons/canonical/dashboard"),
        api.get("/leads-engine/persons/canonical", { params: {
          status: statusFilter || undefined,
          category: categoryFilter || undefined,
          source_id: sourceFilter || undefined, limit: 100 }}),
        api.get("/leads-engine/reservoir/dashboard").catch(
          () => ({ data: null })),
        api.get("/leads-engine/source-factory/registered").catch(
          () => ({ data: null })),
        api.get("/leads-engine/real-data-proof/table").catch(
          () => ({ data: null })),
      ]);
      setDash(d.data);
      setItems(list.data.items || []);
      setReservoir(res.data);
      setFactory(fac.data);
      setProof(tab.data);
    } finally { setLoading(false); }
  }

  async function prepareProof() {
    setPreparing(true);
    try {
      await api.post("/leads-engine/real-data-proof/prepare");
      await load();
    } finally { setPreparing(false); }
  }

  async function openProvenance(person_id) {
    setProvenanceOpen(person_id);
    setProvenanceData(null);
    try {
      const r = await api.get(
        `/leads-engine/real-data-proof/persons/${person_id}/provenances`);
      setProvenanceData(r.data);
    } catch (e) {
      setProvenanceData({ error: e.message });
    }
  }

  async function runFactoryConnector(source_id) {
    setRunning(true);
    try {
      await api.post("/leads-engine/source-factory/run",
        { source_id, limit: 1000 });
      await load();
    } finally { setRunning(false); }
  }

  async function runBenchmark() {
    setRunning(true);
    try {
      const r = await api.post("/leads-engine/sprint11/benchmark");
      setBenchmark(r.data);
      await load();
    } finally { setRunning(false); }
  }

  useEffect(() => { load(); }, [statusFilter, categoryFilter, sourceFilter]);

  const cols = useMemo(() => {
    if (!dash) return [];
    const f = dash.funnel || {};
    return [
      ["persons_identified", "Identifiées", Users, f.persons_identified],
      ["coordinate_found", "Coord. trouvées", Mail, f.coordinate_found],
      ["coordinate_verified", "Vérifiées", ShieldCheck, f.coordinate_verified],
      ["contact_authorized", "Autorisées", UserCheck, f.contact_authorized],
      ["consent_granted", "Consentement", Layers, f.consent_granted],
    ];
  }, [dash]);

  const perSource = dash?.per_source || {};

  return (
    <div data-testid="altiora-radar-persons" className="min-h-screen"
          style={{ background: "#FBF6EA" }}>
      <header className="px-8 py-6"
                style={{ background: NAVY, color: "#FBF6EA" }}>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-[0.24em]"
                style={{ color: GOLD }}>ALTIORA · Radar Personnes (P1)</span>
          <button onClick={prepareProof} data-testid="prepare-rdp"
                  disabled={preparing}
                  className="ml-auto text-[11px] flex items-center gap-1 px-2.5 py-1 rounded"
                  style={{ background: `${GOLD}55`, color: "#FBF6EA",
                             opacity: preparing ? 0.5 : 1 }}>
            <Award className="h-3 w-3" />
            {preparing ? "Préparation…" : "Sprint 13 · Real Data Proof"}
          </button>
          <button onClick={runBenchmark} data-testid="run-sprint11-benchmark"
                  disabled={running}
                  className="text-[11px] flex items-center gap-1 px-2.5 py-1 rounded"
                  style={{ background: `${GOLD}33`, color: "#FBF6EA",
                             opacity: running ? 0.5 : 1 }}>
            <ArrowUpRight className="h-3 w-3" />
            {running ? "Mesure en cours…" : "Lancer benchmark Sprint 11"}
          </button>
          <button onClick={load} data-testid="persons-refresh"
                  className="text-[11px] flex items-center gap-1 px-2 py-1 rounded"
                  style={{ background: `${GOLD}33` }}>
            <RefreshCw className="h-3 w-3" /> Rafraîchir
          </button>
        </div>
        <h1 className="font-serif text-3xl mt-2">
          Funnel personnes canoniques
        </h1>
        <p className="text-[12px] mt-1" style={{ color: GOLD }}>
          Signal → Entité → Personne → Coordonnée → Vérifiée → Autorisée →
          Actionnable · rien de fabriqué.
        </p>
      </header>

      {/* Funnel columns */}
      <div className="px-8 py-4 grid grid-cols-5 gap-3"
            data-testid="persons-funnel">
        {cols.map(([k, label, Icon, value]) => (
          <div key={k} data-testid={`funnel-col-${k}`}
                className="rounded-lg p-3 bg-white"
                style={{ border: `1px solid ${CHAMPAGNE}55` }}>
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em]"
                  style={{ color: SLATE }}>
              <Icon className="h-3 w-3" /> {label}
            </div>
            <div className="text-2xl font-serif mt-1"
                  style={{ color: NAVY }}>{value ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Sprint 12 · Person Reservoir · 4-state lifecycle */}
      {reservoir && (
        <div className="px-8 pt-2" data-testid="reservoir-panel">
          <div className="rounded-xl p-4 bg-white"
                style={{ border: `1px solid ${CHAMPAGNE}55` }}>
            <div className="flex items-baseline justify-between mb-2">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em]"
                    style={{ color: GOLD }}>
                <Database className="h-3 w-3" /> Person Reservoir · cycle 4 états
              </div>
              <div className="text-[10px]" style={{ color: SLATE }}>
                Total : <strong>{reservoir.total_persons}</strong>
                {" · "}Edges signal graph : <strong>{reservoir.signal_graph_total_edges}</strong>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {["DISCOVERED_PERSON","ENRICHED_PERSON","SIGNAL_MATCHED","ACTIONABLE_OPPORTUNITY"].map(state => (
                <div key={state} data-testid={`lifecycle-${state}`}
                      className="rounded p-2"
                      style={{ background: "#FBF6EA",
                                 border: `1px solid ${CHAMPAGNE}55` }}>
                  <div className="text-[10px] font-mono uppercase tracking-[0.14em]"
                        style={{ color: SLATE }}>{state.replace(/_/g," ").toLowerCase()}</div>
                  <div className="text-xl font-serif mt-0.5"
                        style={{ color: NAVY }}>
                    {reservoir.lifecycle?.[state] ?? 0}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sprint 12 · Source Factory */}
      {factory && (
        <div className="px-8 pt-2" data-testid="source-factory-panel">
          <div className="rounded-xl p-4 bg-white"
                style={{ border: `1px solid ${CHAMPAGNE}55` }}>
            <div className="flex items-baseline justify-between mb-2">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em]"
                    style={{ color: GOLD }}>
                <Zap className="h-3 w-3" /> Source Factory · connecteurs enregistrés
              </div>
              <div className="text-[10px]" style={{ color: SLATE }}>
                {factory.registered?.length} connecteur(s)
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {factory.registered?.map(sid => (
                <div key={sid} data-testid={`factory-connector-${sid}`}
                      className="rounded p-2"
                      style={{ background: "#FBF6EA",
                                 border: `1px solid ${CHAMPAGNE}55` }}>
                  <div className="text-[10px] font-mono uppercase tracking-[0.14em]"
                        style={{ color: NAVY }}>{sid}</div>
                  <div className="text-[10px] mt-0.5" style={{ color: SLATE }}>
                    {Object.entries(factory.probes?.[sid] || {})
                      .filter(([k]) => k !== "source_id")
                      .map(([k, v]) => `${k}: ${v}`).join(" · ")}
                  </div>
                  <button
                    disabled={running}
                    onClick={() => runFactoryConnector(sid)}
                    data-testid={`factory-run-${sid}`}
                    className="mt-1.5 text-[10px] px-2 py-0.5 rounded"
                    style={{ background: NAVY, color: "#FBF6EA",
                               opacity: running ? 0.5 : 1 }}>
                    {running ? "…" : "Lancer ingest"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Per-source */}
      <div className="px-8 pb-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.14em] mb-1"
              style={{ color: SLATE }}>Par source</div>
        <div className="flex flex-wrap gap-1.5" data-testid="persons-per-source">
          {Object.entries(perSource).map(([sid, n]) => (
            <button key={sid}
                    onClick={() => setSourceFilter(
                      sourceFilter === sid ? "" : sid)}
                    data-testid={`per-source-${sid}`}
                    className="text-[10px] px-2 py-0.5 rounded border font-mono"
                    style={{
                      background: sourceFilter === sid ? NAVY : "white",
                      color: sourceFilter === sid ? "#FBF6EA" : NAVY,
                      borderColor: `${SLATE}44` }}>
              {sid} · {n}
            </button>
          ))}
        </div>
      </div>

      {/* Sprint 13 · Real Data Proof table */}
      {proof && (
        <div className="px-8 pt-2" data-testid="rdp-panel">
          <div className="rounded-xl p-4 bg-white"
                style={{ border: `2px solid ${GOLD}` }}>
            <div className="flex items-baseline justify-between mb-2">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em]"
                    style={{ color: GOLD }}>
                <Award className="h-3 w-3" /> Sprint 13 · REAL DATA PROOF · table finale
              </div>
              <div className="text-[10px]" style={{ color: SLATE }}>
                Totaux : {proof.totals.persons} personnes · {proof.totals.enriched} enrichies ·{" "}
                {proof.totals.signals} signaux · {proof.totals.opportunities} opps ·{" "}
                <strong>{proof.totals.contactable} contactables</strong>
              </div>
            </div>
            <table className="w-full text-[11px]" style={{ color: NAVY }}
                    data-testid="rdp-table">
              <thead>
                <tr style={{ color: SLATE }}>
                  <th className="text-left font-mono uppercase tracking-[0.14em] p-1">Source</th>
                  <th className="text-left font-mono uppercase tracking-[0.14em] p-1">Type</th>
                  <th className="text-right font-mono uppercase tracking-[0.14em] p-1">Personnes</th>
                  <th className="text-right font-mono uppercase tracking-[0.14em] p-1">Enrichies</th>
                  <th className="text-right font-mono uppercase tracking-[0.14em] p-1">Signaux</th>
                  <th className="text-right font-mono uppercase tracking-[0.14em] p-1">Opps</th>
                  <th className="text-right font-mono uppercase tracking-[0.14em] p-1">Contactables</th>
                  <th className="text-right font-mono uppercase tracking-[0.14em] p-1">Coût</th>
                </tr>
              </thead>
              <tbody>
                {proof.rows.map((r) => (
                  <tr key={r.source_id}
                        data-testid={`rdp-row-${r.source_id}`}
                        style={{ borderTop: `1px solid ${CHAMPAGNE}44` }}>
                    <td className="p-1 font-mono">{r.source_id}</td>
                    <td className="p-1 text-[10px]" style={{ color: SLATE }}>
                      {r.source_type}
                    </td>
                    <td className="p-1 text-right">{r.persons}</td>
                    <td className="p-1 text-right">{r.enriched}</td>
                    <td className="p-1 text-right">{r.signals}</td>
                    <td className="p-1 text-right">{r.opportunities}</td>
                    <td className="p-1 text-right font-bold"
                         style={{ color: r.contactable > 0 ? "#0F5E39" : SLATE }}>
                      {r.contactable}
                    </td>
                    <td className="p-1 text-right text-[10px] font-mono">
                      {r.cost}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[10px] italic mt-2" style={{ color: SLATE }}>
              {proof.disclaimer}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="px-8 py-2 flex flex-wrap items-center gap-3"
            style={{ color: SLATE }}>
        <Filter className="h-3 w-3" />
        <select value={statusFilter}
                 onChange={(e) => setStatusFilter(e.target.value)}
                 data-testid="persons-filter-status"
                 className="text-[11px] rounded px-2 py-1 border">
          <option value="">Statut : tous</option>
          <option value="coordinate_found">Coord. trouvées</option>
          <option value="coordinate_verified">Coord. vérifiées</option>
          <option value="consent_granted">Consentement</option>
        </select>
        <select value={categoryFilter}
                 onChange={(e) => setCategoryFilter(e.target.value)}
                 data-testid="persons-filter-category"
                 className="text-[11px] rounded px-2 py-1 border">
          <option value="">Catégorie : toutes</option>
          {Object.entries(CAT_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <span className="ml-auto text-[11px]">
          {items.length} personne{items.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Benchmark result */}
      {benchmark && (
        <div className="px-8 py-3 mb-2" data-testid="benchmark-result">
          <div className="rounded-xl p-4 bg-white"
                style={{ border: `2px solid ${GOLD}` }}>
            <div className="text-[10px] font-mono uppercase tracking-[0.14em]"
                  style={{ color: GOLD }}>
              Sprint 11 · Benchmark mesuré
            </div>
            <div className="grid grid-cols-3 gap-4 mt-2 text-[11px]"
                  style={{ color: NAVY }}>
              {Object.entries(benchmark.cohorts).map(([name, c]) => (
                <div key={name} className="rounded p-2"
                      style={{ background: "#FBF6EA" }}
                      data-testid={`cohort-${name}`}>
                  <div className="font-serif text-[13px]">{name}</div>
                  <div className="text-[10px] mt-1">
                    Personnes : <strong>{c.persons_identified}</strong>
                  </div>
                  {c.email_found !== undefined && (
                    <div className="text-[10px]">
                      Emails trouvés : <strong>{c.email_found}</strong>
                      {" · "}vérifiés : <strong>{c.email_verified ?? 0}</strong>
                    </div>
                  )}
                  {c.phone_found !== undefined && (
                    <div className="text-[10px]">
                      Phones : <strong>{c.phone_found}</strong>
                      {" · "}vérifiés : <strong>{c.phone_verified ?? 0}</strong>
                    </div>
                  )}
                  {c.contact_authorized !== undefined && (
                    <div className="text-[10px]">
                      Autorisées : <strong>{c.contact_authorized}</strong>
                    </div>
                  )}
                  {c.note && (
                    <div className="text-[10px] italic mt-1"
                          style={{ color: SLATE }}>{c.note}</div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-[10px] italic mt-2" style={{ color: SLATE }}>
              {benchmark.disclaimer}
            </div>
          </div>
        </div>
      )}

      {/* Persons list */}
      <div className="px-8 pb-10 space-y-2">
        {loading && <div style={{ color: SLATE }}>Chargement…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-xl bg-white p-6 text-center"
                style={{ border: `1px dashed ${CHAMPAGNE}88` }}>
            <AlertTriangle className="h-6 w-6 mx-auto mb-2"
                             style={{ color: GOLD }} />
            <div className="font-serif text-[14px]" style={{ color: NAVY }}>
              Aucune personne pour ces filtres.
            </div>
            <div className="text-[11px] mt-1" style={{ color: SLATE }}>
              Lancez le benchmark ou soumettez un lead consenti via /app/campaigns.
            </div>
          </div>
        )}
        {!loading && items.map((p, i) => {
          const cat = CAT_LABELS[p.category] || CAT_LABELS.OTHER;
          const dcClass = p.data_class || "?";
          const dcColor = dcClass === "REAL" ? "#0F5E39"
                            : dcClass === "TEST" ? "#B91C1C"
                            : "#F59E0B";
          return (
            <article key={p.person_id}
                      data-testid={`person-card-${i}`}
                      className="rounded-xl bg-white p-3 grid gap-3"
                      style={{ border: `1px solid ${CHAMPAGNE}55`,
                                 gridTemplateColumns: "1.3fr 1fr 1fr 0.5fr" }}>
              <div>
                <div className="flex items-center gap-1.5">
                  <h3 className="font-serif text-[15px]" style={{ color: NAVY }}>
                    {p.full_name || `${p.first_name || ""} ${p.last_name || ""}`}
                  </h3>
                  <span className="text-[9px] px-1.5 py-0.5 rounded font-mono uppercase"
                         style={{ color: dcColor, background: `${dcColor}22`,
                                    border: `1px solid ${dcColor}55` }}
                         data-testid={`person-dataclass-${i}`}>
                    {dcClass}
                  </span>
                </div>
                <div className="text-[11px]" style={{ color: SLATE }}>
                  {p.title || p.profession || "—"}{" "}
                  {p.organization && <>· {p.organization}</>}
                </div>
                <div className="text-[10px] mt-0.5" style={{ color: SLATE }}>
                  {[p.city, p.province, p.country].filter(Boolean).join(" · ")}
                </div>
                <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase"
                        style={{ background: cat.bg, color: cat.color }}>
                  {cat.label}
                </span>
              </div>
              <div className="text-[11px]" style={{ color: SLATE }}>
                <div className="font-mono uppercase text-[10px] tracking-[0.14em] mb-0.5">
                  Provenance
                </div>
                <div className="font-mono text-[10px]" style={{ color: NAVY }}>
                  {p.source_id}
                </div>
                {p.source_url && (
                  <a href={p.source_url} target="_blank" rel="noreferrer"
                      className="text-[10px] underline">
                    voir la source
                  </a>
                )}
                <div className="text-[10px] mt-1">
                  segments : {(p.segment_signals || []).join(", ") || "—"}
                </div>
                <div className="text-[10px] mt-0.5">
                  Confiance : {(p.confidence_score * 100).toFixed(0)} %
                </div>
              </div>
              <div className="text-[11px]" style={{ color: SLATE }}>
                <div className="font-mono uppercase text-[10px] tracking-[0.14em] mb-0.5">
                  Coordonnées
                </div>
                {p.email && (
                  <div className="font-mono text-[10px]" style={{ color: NAVY }}>
                    <Mail className="h-2.5 w-2.5 inline" /> {p.email}
                  </div>
                )}
                {p.phone && (
                  <div className="font-mono text-[10px]" style={{ color: NAVY }}>
                    <Phone className="h-2.5 w-2.5 inline" /> {p.phone}
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-1">
                  <ChannelPill channel="email"
                                 status={p.email_contactability_status} />
                  <ChannelPill channel="phone"
                                 status={p.phone_contactability_status} />
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-mono uppercase tracking-[0.14em]"
                      style={{ color: SLATE }}>3 niveaux</div>
                <div className="text-[10px] mt-1"
                      style={{ color: p.coordinate_found ? "#0F5E39" : SLATE }}>
                  trouvé : {p.coordinate_found ? "✓" : "—"}
                </div>
                <div className="text-[10px]"
                      style={{ color: p.coordinate_verified ? "#1E3A70" : SLATE }}>
                  vérifié : {p.coordinate_verified ? "✓" : "—"}
                </div>
                <div className="text-[10px] font-bold"
                      style={{ color: p.contact_authorized ? "#10B981" : SLATE }}>
                  autorisé : {p.contact_authorized ? "✓" : "—"}
                </div>
                {p.consent_status === "GRANTED" && (
                  <div className="text-[10px] mt-1 italic"
                        style={{ color: GOLD }}>consentement enregistré</div>
                )}
                <button onClick={() => openProvenance(p.person_id)}
                         data-testid={`provenance-btn-${i}`}
                         className="text-[10px] mt-1.5 px-1.5 py-0.5 rounded border font-mono"
                         style={{ background: "white", color: NAVY,
                                    borderColor: `${NAVY}55` }}>
                  <Fingerprint className="h-2.5 w-2.5 inline" /> provenances
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {/* Provenance modal */}
      {provenanceOpen && (
        <div data-testid="provenance-modal"
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ background: "rgba(15,23,42,0.7)" }}
              onClick={() => setProvenanceOpen(null)}>
          <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl"
                style={{ background: "#FBF6EA",
                           border: `2px solid ${GOLD}` }}
                onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 flex items-center justify-between sticky top-0"
                  style={{ background: NAVY, color: "#FBF6EA" }}>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.24em]"
                      style={{ color: GOLD }}>ALTIORA · Provenances append-only</div>
                <h3 className="font-serif text-[16px]">
                  Personne · {provenanceOpen}
                </h3>
              </div>
              <button onClick={() => setProvenanceOpen(null)}
                       data-testid="provenance-close"
                       className="text-lg px-2">×</button>
            </div>
            <div className="p-4 space-y-4" style={{ color: NAVY }}>
              {!provenanceData ? (<div>Chargement…</div>) : (
                <>
                  <div className="text-[11px]">
                    Source primaire : <strong>{provenanceData.primary_source_id}</strong>
                    {" · "}data_class : {provenanceData.data_class}
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.14em]"
                         style={{ color: SLATE }}>
                      Provenances append-only ({provenanceData.provenances?.length || 0})
                    </div>
                    <div className="mt-1 space-y-1">
                      {(provenanceData.provenances || []).map((p, k) => (
                        <div key={k} className="text-[11px] rounded p-1.5"
                              style={{ background: "white",
                                         border: `1px solid ${CHAMPAGNE}55` }}>
                          <span className="font-mono">{p.source_id}</span>
                          {" · "}{p.observed_at?.slice(0,10)}
                          {" · "}méthode: {p.method}
                          {p.source_url && (
                            <> · <a href={p.source_url} target="_blank"
                                   rel="noreferrer" style={{ color: GOLD }}>
                              voir</a></>)}
                        </div>
                      ))}
                      {!provenanceData.provenances?.length && (
                        <div className="text-[10px] italic"
                              style={{ color: SLATE }}>
                          Aucune provenance additionnelle enregistrée
                          (dedup cross-source non déclenché).
                        </div>)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.14em]"
                         style={{ color: SLATE }}>
                      Signaux graph ({provenanceData.signals?.length || 0})
                    </div>
                    <div className="mt-1 space-y-1">
                      {(provenanceData.signals || []).map((s, k) => (
                        <div key={k} className="text-[11px] rounded p-1.5"
                              style={{ background: "white",
                                         border: `1px solid ${CHAMPAGNE}55` }}>
                          <span className="font-mono">{s.signal_type}</span>
                          {" · "}{s.source_id}
                          {" · "}{s.observed_at?.slice(0,10)}
                          {" · confiance "}{Math.round((s.confidence||0)*100)}%
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
